/**
 * CreateRecordActionConfig — T12.D.cm cross-module actions.
 *
 * Sub-form używany przez ActionButtonConfigurator gdy actionType === 'create_record'.
 * Konfiguracja akcji „Utwórz rekord w innym module" (np. „Wystaw fakturę z serwisu").
 *
 * Po kliknięciu przycisku (runtime) backend zwraca redirectUrl do
 * /modules/<target_slug>/records/new z prefilem — user widzi formularz z
 * wypełnionymi polami, edytuje, zatwierdza Zapisem (Flow 1).
 *
 * UX (wariant C — hybryda jak SellCall):
 *   1. User wybiera docelowy moduł
 *   2. Auto-mapping przy zmianie modułu (slug match + field_type fallback)
 *   3. User może nadpisać mapowanie per pole (dropdown: pole z rekordu / statyczna wartość / puste)
 *   4. Opcjonalnie wybiera kategorię docelową (jeśli moduł ma kategorie)
 *
 * Auto-mapping reguły:
 *   - exact slug match — `klient_id` → `klient_id` (priorytet 1)
 *   - field_type unique match — jeden pole `email` w obu modułach (priorytet 2)
 *   - brak match'a — `static` z pustą wartością (user może doustawić)
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { Input } from '@/components/Input';
import { api } from '@/lib/api';

// ── Typy ─────────────────────────────────────────────────────────────────────

export type MappingSource = 'field' | 'static';

export interface FieldMapping {
  targetFieldSlug: string;
  source: MappingSource;
  sourceFieldSlug?: string;
  staticValue?: string;
}

export interface CreateRecordConfig {
  targetModuleId: string;
  targetCategoryId: string | null;
  fieldMappings: FieldMapping[];
}

interface ModuleListItem {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
}

interface ModuleDetail {
  id: string;
  slug: string;
  name: string;
  fields: ModuleField[];
}

interface Category {
  id: string;
  name: string;
  color: string;
}

// ── Auto-mapping helper ───────────────────────────────────────────────────────

/**
 * Auto-mapping pól z modułu źródła na moduł docelowy.
 * Reguły (priorytetowe):
 *  1. Exact slug match
 *  2. Field type match (unique — tylko jedno pole o danym typie w source)
 *  3. Brak match'a — static z pustą wartością
 */
export function autoMapFields(
  sourceFields: ModuleField[],
  targetFields: ModuleField[],
): FieldMapping[] {
  return targetFields.map((target): FieldMapping => {
    // 1. exact slug match
    const exact = sourceFields.find((s) => s.slug === target.slug);
    if (exact) {
      return { targetFieldSlug: target.slug, source: 'field', sourceFieldSlug: exact.slug };
    }

    // 2. unique field_type match
    const sameType = sourceFields.filter((s) => s.fieldType === target.fieldType);
    if (sameType.length === 1 && sameType[0]) {
      return { targetFieldSlug: target.slug, source: 'field', sourceFieldSlug: sameType[0].slug };
    }

    // 3. brak match'a
    return { targetFieldSlug: target.slug, source: 'static', staticValue: '' };
  });
}

// ── Component ────────────────────────────────────────────────────────────────

interface CreateRecordActionConfigProps {
  /** Pola modułu źródła (skąd kopiujemy). */
  sourceFields: ModuleField[];
  /** ID modułu źródła — wykluczamy z listy modułów docelowych. */
  currentModuleId: string;
  /** Bieżąca konfiguracja akcji. */
  value: CreateRecordConfig;
  /** Callback przy zmianie konfiguracji. */
  onChange: (value: CreateRecordConfig) => void;
}

export function CreateRecordActionConfig({
  sourceFields,
  currentModuleId,
  value,
  onChange,
}: CreateRecordActionConfigProps) {
  const { t } = useTranslation();

  const inputCls =
    'w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1.5 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500';
  const labelCls = 'mb-1 block text-xs text-text-secondary';

  // Lista wszystkich modułów (wykluczamy bieżący)
  const modulesQuery = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: ModuleListItem[] }>('/modules');
      return res.data.modules;
    },
  });
  const availableModules = useMemo(
    () => (modulesQuery.data ?? []).filter((m) => m.id !== currentModuleId),
    [modulesQuery.data, currentModuleId],
  );

  // Szczegóły wybranego docelowego modułu (pola)
  const targetModuleQuery = useQuery({
    queryKey: ['modules', value.targetModuleId, 'detail'],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleDetail }>(`/modules/${value.targetModuleId}`);
      return res.data.module;
    },
    enabled: !!value.targetModuleId,
  });
  const targetFields = useMemo(
    () => targetModuleQuery.data?.fields ?? [],
    [targetModuleQuery.data],
  );

  // Kategorie docelowego modułu
  const categoriesQuery = useQuery({
    queryKey: ['module-categories', value.targetModuleId],
    queryFn: async () => {
      const res = await api.get<{ categories: Category[] }>(
        `/modules/${value.targetModuleId}/categories`,
      );
      return res.data.categories;
    },
    enabled: !!value.targetModuleId,
  });
  const categories = categoriesQuery.data ?? [];

  // Auto-mapping przy zmianie targetModuleId (gdy mappings puste)
  useEffect(() => {
    if (!value.targetModuleId) return;
    if (targetFields.length === 0) return;
    if (value.fieldMappings.length > 0) return;
    const auto = autoMapFields(sourceFields, targetFields);
    onChange({ ...value, fieldMappings: auto });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.targetModuleId, targetFields.length]);

  const setTargetModule = (newId: string) => {
    // Reset mapping + category przy zmianie modułu
    onChange({
      targetModuleId: newId,
      targetCategoryId: null,
      fieldMappings: [],
    });
  };

  const setCategory = (catId: string) => {
    onChange({ ...value, targetCategoryId: catId || null });
  };

  const updateMapping = (targetFieldSlug: string, patch: Partial<FieldMapping>) => {
    const next = value.fieldMappings.map((m) =>
      m.targetFieldSlug === targetFieldSlug ? { ...m, ...patch } : m,
    );
    onChange({ ...value, fieldMappings: next });
  };

  const runAutoMapping = () => {
    const auto = autoMapFields(sourceFields, targetFields);
    onChange({ ...value, fieldMappings: auto });
  };

  return (
    <div className="space-y-3">
      {/* Wybór modułu docelowego */}
      <div>
        <label className={labelCls}>
          {t('tenant.modules.actionButtons.createRecord.targetModule')}
        </label>
        <Dropdown
          value={value.targetModuleId}
          onChange={(v) => setTargetModule(v)}
          className={inputCls}
          data-testid="cr-target-module"
          options={[
            { value: '', label: t('tenant.modules.actionButtons.createRecord.selectModule') },
            ...availableModules.map((m) => ({ value: m.id, label: m.name })),
          ]}
        />
      </div>

      {/* Wybór kategorii (jeśli moduł ma kategorie) */}
      {value.targetModuleId && categories.length > 0 && (
        <div>
          <label className={labelCls}>
            {t('tenant.modules.actionButtons.createRecord.targetCategory')}
            <span className="ml-1 text-text-muted">
              ({t('tenant.modules.actionButtons.createRecord.optional')})
            </span>
          </label>
          <Dropdown
            value={value.targetCategoryId ?? ''}
            onChange={(v) => setCategory(v)}
            className={inputCls}
            data-testid="cr-target-category"
            options={[
              { value: '', label: t('tenant.modules.actionButtons.createRecord.noCategory') },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
        </div>
      )}

      {/* Mapowanie pól */}
      {value.targetModuleId && targetFields.length > 0 && (
        <div className="max-w-3xl space-y-2 rounded-md border border-border-primary bg-bg-primary p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-text-secondary">
              {t('tenant.modules.actionButtons.createRecord.fieldMappings')}
              <span className="ml-1 text-text-muted">({targetFields.length})</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={runAutoMapping}
              data-testid="cr-auto-map-btn"
            >
              <RefreshCw className="h-3 w-3" />
              {t('tenant.modules.actionButtons.createRecord.autoMap')}
            </Button>
          </div>

          <p className="text-xs text-text-muted">
            {t('tenant.modules.actionButtons.createRecord.mappingsHint')}
          </p>

          {/* Nagłówki kolumn */}
          <div className="grid grid-cols-[1fr_24px_110px_1fr] items-center gap-2 border-b border-border-primary pb-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
            <span>{t('tenant.modules.actionButtons.createRecord.colTarget')}</span>
            <span aria-hidden="true" />
            <span>{t('tenant.modules.actionButtons.createRecord.colSource')}</span>
            <span>{t('tenant.modules.actionButtons.createRecord.colValue')}</span>
          </div>

          <div className="divide-y divide-border-primary/50">
            {targetFields.map((tf, idx) => {
              const mapping = value.fieldMappings.find((m) => m.targetFieldSlug === tf.slug) ?? {
                targetFieldSlug: tf.slug,
                source: 'static' as MappingSource,
                staticValue: '',
              };

              // Ostrzeżenie: auto-mapping field_type match (NIE slug match) — biznesowo wątpliwe
              const isTypeOnlyMatch =
                mapping.source === 'field' &&
                mapping.sourceFieldSlug &&
                mapping.sourceFieldSlug !== tf.slug;

              return (
                <div
                  key={tf.id}
                  className={
                    'grid grid-cols-[1fr_24px_110px_1fr] items-center gap-2 px-1.5 py-1.5 text-xs ' +
                    (idx % 2 === 0 ? 'bg-transparent' : 'bg-bg-secondary/40')
                  }
                  data-testid={`cr-mapping-${tf.slug}`}
                >
                  {/* Pole docelowe */}
                  <div className="min-w-0 flex items-center gap-1.5" title={tf.slug}>
                    <span className="truncate font-medium text-text-primary">{tf.name}</span>
                    {tf.isRequired && (
                      <span className="text-danger-500" aria-label="wymagane">
                        *
                      </span>
                    )}
                    <span className="shrink-0 text-text-muted">({tf.fieldType})</span>
                    {isTypeOnlyMatch && (
                      <AlertTriangle
                        className="h-3 w-3 shrink-0 text-warning-500"
                        aria-label={t('tenant.modules.actionButtons.createRecord.typeMatchWarning')}
                      />
                    )}
                  </div>

                  {/* Strzałka kierunkowa */}
                  <ArrowRight className="h-3 w-3 shrink-0 text-text-muted" aria-hidden="true" />

                  {/* Source type */}
                  <Dropdown
                    value={mapping.source}
                    onChange={(v) =>
                      updateMapping(tf.slug, {
                        source: v as MappingSource,
                        // Reset sub-values przy zmianie typu
                        sourceFieldSlug: undefined,
                        staticValue: v === 'static' ? '' : undefined,
                      })
                    }
                    className="rounded border border-border-primary bg-bg-primary px-1.5 py-1 text-xs text-text-primary"
                    data-testid={`cr-source-type-${tf.slug}`}
                    options={[
                      {
                        value: 'field',
                        label: t('tenant.modules.actionButtons.createRecord.sourceTypes.field'),
                      },
                      {
                        value: 'static',
                        label: t('tenant.modules.actionButtons.createRecord.sourceTypes.static'),
                      },
                    ]}
                  />

                  {/* Source value — różne wg source type */}
                  {mapping.source === 'field' ? (
                    <Dropdown
                      value={mapping.sourceFieldSlug ?? ''}
                      onChange={(v) =>
                        updateMapping(tf.slug, {
                          sourceFieldSlug: v || undefined,
                        })
                      }
                      className="min-w-0 rounded border border-border-primary bg-bg-primary px-1.5 py-1 text-xs text-text-primary"
                      data-testid={`cr-source-field-${tf.slug}`}
                      options={[
                        {
                          value: '',
                          label: t('tenant.modules.actionButtons.createRecord.selectSourceField'),
                        },
                        ...sourceFields.map((sf) => ({
                          value: sf.slug,
                          label: `${sf.name} (${sf.fieldType})`,
                        })),
                      ]}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={mapping.staticValue ?? ''}
                      onChange={(e) => updateMapping(tf.slug, { staticValue: e.target.value })}
                      placeholder={t(
                        'tenant.modules.actionButtons.createRecord.staticValuePlaceholder',
                      )}
                      className="min-w-0 rounded border border-border-primary bg-bg-primary px-1.5 py-1 text-xs text-text-primary"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading state */}
      {value.targetModuleId && targetModuleQuery.isLoading && (
        <p className="text-xs text-text-muted">{t('common.loading')}</p>
      )}
    </div>
  );
}
